import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { ApiError, CombineGroup, CombinePreview, TargetType } from '../../services/learningApi';
import { useApis } from './common';
import { DiffView } from './DiffView';

interface Props {
  open: boolean;
  ids: string[];
  titles: Record<string, string>;
  targetType: TargetType;
  targetId: string;
  nodeLabel: (id: string | null | undefined) => string | null;
  onClose: () => void;
  onApplied: (message: string) => void;
}

function show(v: any): string {
  if (v === null || v === undefined || v === '') return '(not set)';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

const GroupCard: React.FC<{
  g: CombineGroup;
  text: string;
  titles: Record<string, string>;
  onText: (t: string) => void;
}> = ({ g, text, titles, onText }) => {
  const [view, setView] = useState<'diff' | 'edit'>('diff');
  const edited = text !== g.combined;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {g.function_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          new version from v{g.version}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {g.method === 'merged' && <Chip size="small" color="success" variant="outlined" label="Merged directly — the edits don’t overlap" />}
        {g.method === 'compiled' && <Chip size="small" color="info" variant="outlined" icon={<AutoFixHighIcon />} label={`Combined by AI${g.model ? ` (${g.model})` : ''}`} />}
        {edited && <Chip size="small" label="Edited by you" />}
      </Stack>

      {g.suggestion_ids.length > 0 ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Includes: {g.suggestion_ids.map((id) => `“${titles[id] || id}”`).join(', ')}
        </Typography>
      ) : (
        <Alert severity="info" sx={{ mb: 1 }}>
          Nothing left to change in this function.
        </Alert>
      )}
      {g.skipped.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Left out (they stay open):
          {g.skipped.map((s) => (
            <Box key={s.id} component="div">
              • “{s.title}”{s.reason ? ` — ${s.reason}` : ''}
            </Box>
          ))}
        </Alert>
      )}
      {g.notes.length > 0 && (
        <Alert severity="info" icon={false} sx={{ mb: 1 }}>
          {g.notes.map((n, i) => (
            <Box key={i} component="div">
              • {n}
            </Box>
          ))}
        </Alert>
      )}
      {g.dropped_lines.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          This removes {g.dropped_lines.length} existing line{g.dropped_lines.length === 1 ? '' : 's'} entirely. Check that’s intended, or put them back under “Edit”:
          {g.dropped_lines.map((l, i) => (
            <Box key={i} component="div" sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
              − {l}
            </Box>
          ))}
        </Alert>
      )}

      <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)} sx={{ mb: 1 }}>
        <ToggleButton value="diff">Changes vs. v{g.version}</ToggleButton>
        <ToggleButton value="edit">Edit before applying</ToggleButton>
      </ToggleButtonGroup>
      {view === 'diff' ? (
        <DiffView before={g.current} after={text} />
      ) : (
        <TextField multiline fullWidth minRows={10} maxRows={28} value={text} onChange={(e) => onText(e.target.value)} InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }} />
      )}
    </Paper>
  );
};

export const CombineSuggestionsDialog: React.FC<Props> = ({ open, ids, titles, targetType, targetId, nodeLabel, onClose, onApplied }) => {
  const apis = useApis();
  const [preview, setPreview] = useState<CombinePreview | null>(null);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [usingAi, setUsingAi] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const combine = useCallback(async (useAi: boolean) => {
    setLoading(true);
    setUsingAi(useAi);
    setError(null);
    setStale(false);
    try {
      const res = await apis.learning.combineSuggestions(targetType, targetId, ids, useAi);
      setPreview(res);
      setTexts(Object.fromEntries(res.groups.map((g) => [g.function_id, g.combined])));
    } catch (e: any) {
      setPreview(null);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apis, targetType, targetId, ids]);

  useEffect(() => {
    if (open) combine(false);
    else {
      setPreview(null);
      setError(null);
    }
  }, [open, combine]);

  const skipped = new Set((preview?.groups || []).flatMap((g) => g.skipped.map((s) => s.id)));
  const applyIds = ids.filter((id) => !skipped.has(id));
  const blocked = !preview || preview.errors.length > 0 || applyIds.length === 0 || (preview.groups || []).some((g) => !(texts[g.function_id] || '').trim());
  const hasMerged = (preview?.groups || []).some((g) => g.method === 'merged');
  const hasCompiled = (preview?.groups || []).some((g) => g.method === 'compiled');

  const apply = async () => {
    if (!preview) return;
    setApplying(true);
    setError(null);
    try {
      const res = await apis.learning.applySuggestionsBatch(
        targetType,
        targetId,
        applyIds,
        preview.groups.filter((g) => g.suggestion_ids.length > 0).map((g) => ({ function_id: g.function_id, text: texts[g.function_id] })),
      );
      const parts = [`Applied ${res.applied} suggestion${res.applied === 1 ? '' : 's'} together`];
      if (res.versions.length) parts.push(`created ${res.versions.map((v) => `${v.function_name} v${v.new_version}`).join(', ')}`);
      if (res.already_included) parts.push(`${res.already_included} already in the live version, marked done`);
      onApplied(`${parts.join(' — ')}.`);
    } catch (e: any) {
      setError(e.message);
      setStale(e instanceof ApiError && e.status === 409);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Combine &amp; apply {ids.length} suggestion{ids.length === 1 ? '' : 's'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Everything selected becomes one new version. Edits in different parts of the instructions are merged directly; edits to the same lines are combined
          by AI. Nothing is saved until you click Apply, and it can be undone afterwards.
        </Typography>
        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4, justifyContent: 'center' }}>
            <CircularProgress size={22} />
            <Typography color="text.secondary">{usingAi ? 'Combining with AI — this can take up to a minute…' : 'Combining… (falls back to AI if edits overlap)'}</Typography>
          </Stack>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={stale ? <Button color="inherit" size="small" onClick={() => combine(false)}>Combine again</Button> : undefined}
          >
            {error}
          </Alert>
        )}
        {!loading && preview && (
          <>
            {preview.errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                These can’t be applied together. Change the selection and try again:
                {preview.errors.map((e, i) => (
                  <Box key={i} component="div">
                    • {e}
                  </Box>
                ))}
              </Alert>
            )}
            {preview.groups.map((g) => (
              <GroupCard key={g.function_id} g={g} titles={titles} text={texts[g.function_id] ?? g.combined} onText={(t) => setTexts((x) => ({ ...x, [g.function_id]: t }))} />
            ))}
            {preview.settings.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Setting changes
                </Typography>
                {preview.settings.map((s) => (
                  <Typography key={s.suggestion_id} variant="body2">
                    {s.node_id ? `${nodeLabel(s.node_id) || s.node_id}: ` : ''}
                    {s.field} {show(s.from)} → {show(s.to)} <Typography component="span" variant="caption" color="text.secondary">(“{s.title}”)</Typography>
                  </Typography>
                ))}
              </Paper>
            )}
            {[...preview.already_included, ...preview.groups.flatMap((g) => g.already_included)].length > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Already in the live version, so these are just marked done:{' '}
                {[...preview.already_included, ...preview.groups.flatMap((g) => g.already_included)].map((id) => `“${titles[id] || id}”`).join(', ')}
              </Alert>
            )}
            {preview.credits_used > 0 && (
              <Typography variant="caption" color="text.secondary">
                AI combining used {preview.credits_used} credits.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {preview && !loading && preview.errors.length === 0 && (hasMerged || hasCompiled) && (
          <Button startIcon={<AutoFixHighIcon />} onClick={() => combine(true)} disabled={applying} sx={{ mr: 'auto' }}>
            {hasCompiled && !hasMerged ? 'Combine with AI again' : 'Combine with AI instead'}
          </Button>
        )}
        <Button onClick={onClose} disabled={applying}>
          Cancel
        </Button>
        <Button variant="contained" onClick={apply} disabled={blocked || loading || applying} startIcon={applying ? <CircularProgress size={14} /> : undefined}>
          Apply {applyIds.length} together
        </Button>
      </DialogActions>
    </Dialog>
  );
};
